'use client';

import { useState } from 'react';
import { useHotkeys } from '@mantine/hooks';

export type HotkeyItem =
  | [string, (event: KeyboardEvent) => void]
  | [string, (event: KeyboardEvent) => void, { preventDefault?: boolean }];

interface UseTableKeyboardNavigationOptions<T extends Record<string, unknown>> {
  data: T[];
  rowSelection: Record<string, boolean>;
  setRowSelection: (
    selection:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  table: {
    getSelectedRowModel: () => {
      rows: { original: T }[];
    };
    resetRowSelection: () => void;
  };
  id: keyof T; // Row ID field name
  customHotkeys?: HotkeyItem[];
}

/**
 * ### useTableKeyboardNavigation
 *
 * @description
 * Reusable hook that provides keyboard navigation functionality for tables.
 * Supports custom hotkeys for domain-specific actions.
 *
 * @example
 * const { focusedRowIndex } = useTableKeyboardNavigation({
 *   data, // Table data
 *   rowSelection, // Row selection state
 *   setRowSelection, // Function to update row selection
 *   table, // Table instance
 *   id: 'id', // The field name for row IDs (e.g., 'id', 'userId', 'highlighterId')
 *   customHotkeys: [ // Custom hotkeys
 *     ['a', () => handleApprove()], // Approve selected rows
 *     ['r', () => handleReject()], // Reject selected rows
 *     ['d', () => handleDelete()], // Delete selected rows
 *     ['ctrl+shift+a', (event) => { event.preventDefault(); selectAll(); }] // Select all rows
 *   ]
 * });
 *
 * Default hotkeys:
 * - 'j' / ArrowDown: Move focus down
 * - 'k' / ArrowUp: Move focus up
 * - Space / 's': Toggle row selection
 *
 * - Escape: Clear all selections
 *
 * @param options - Configuration options for keyboard navigation
 * @returns Object containing focusedRowIndex.
 */
export const useTableKeyboardNavigation = <T extends Record<string, unknown>>(
  options: UseTableKeyboardNavigationOptions<T>,
) => {
  const { data, rowSelection, setRowSelection, table, id, customHotkeys = [] } = options;

  // Start unfocused (-1): no row is highlighted until the user navigates with j/k/arrows.
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);

  const moveFocusUp = () => {
    setFocusedRowIndex((prev: number) => {
      const newIndex = Math.max(0, prev - 1);
      return newIndex;
    });
  };

  const moveFocusDown = () => {
    setFocusedRowIndex((prev: number) => {
      const newIndex = Math.min(data.length - 1, prev + 1);
      return newIndex;
    });
  };

  const toggleRowSelection = () => {
    if (data[focusedRowIndex]) {
      const rowId = String(data[focusedRowIndex][id]);
      const isSelected = rowSelection[rowId];

      setRowSelection((prev: Record<string, boolean>) => ({
        ...prev,
        [rowId]: !isSelected,
      }));
    }
  };

  const defaultHotkeys: HotkeyItem[] = [
    [
      'j',
      () => {
        moveFocusDown();
      },
    ],
    [
      'k',
      () => {
        moveFocusUp();
      },
    ],
    [
      'ArrowUp',
      (event: KeyboardEvent) => {
        event.preventDefault();
        moveFocusUp();
      },
    ],
    [
      'ArrowDown',
      (event: KeyboardEvent) => {
        event.preventDefault();
        moveFocusDown();
      },
    ],
    [
      'Space',
      (event: KeyboardEvent) => {
        event.preventDefault();
        toggleRowSelection();
      },
    ],
    [
      's',
      (event: KeyboardEvent) => {
        event.preventDefault();
        toggleRowSelection();
      },
    ],
    ['Escape', () => table.resetRowSelection()],
  ];

  useHotkeys([...defaultHotkeys, ...customHotkeys]);

  return {
    focusedRowIndex,
  };
};
